// ===========================================
// ADMIN HTTP CLIENT
// ===========================================

import axios from 'axios';
import { adminAuthService } from './authService';

/**
 * API configuration for admin endpoints
 */
export const ADMIN_API_CONFIG = {
  baseURL: import.meta.env.VITE_API_BASE_URL ?? 'https://api.tunistravels.com/api',
  timeout: 15000,
};

/**
 * Axios instance configured for admin API requests
 * Includes authentication token handling and refresh logic
 */
export const adminHttp = axios.create({
  baseURL: ADMIN_API_CONFIG.baseURL,
  timeout: ADMIN_API_CONFIG.timeout,
});

// Request interceptor: Add auth token to requests
adminHttp.interceptors.request.use((config) => {
  const auth = adminAuthService.getStoredAuth();
  if (auth?.token) {
    config.headers.Authorization = `Bearer ${auth.token}`;
  }
  return config;
});

// Response interceptor: Handle token refresh on 401
adminHttp.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as {
      _retry?: boolean;
      headers?: Record<string, string>;
    };

    // Attempt token refresh on 401 unauthorized
    if (error.response?.status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true;

      try {
        const refreshed = await adminAuthService.refreshTokens();
        if (refreshed?.token) {
          if (!originalRequest.headers) {
            originalRequest.headers = {};
          }
          originalRequest.headers.Authorization = `Bearer ${refreshed.token}`;
          return adminHttp(originalRequest);
        }
      } catch {
        // Refresh failed, redirect to login
        adminAuthService.clearStoredAuth();
        window.location.href = '/admin/login';
      }
    }

    return Promise.reject(error);
  }
);
